import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { apiService } from '@/service/api'
import { fetchThumbnailWithRetry } from '@/lib/documentThumbnail'
import { useTranslation } from '@/i18n'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import ZoomControls from '@/components/intueri/ZoomControls'
import { useZoom } from '@/hooks/useZoom'
import { cn } from '@/lib/utils'
import { SpreadsheetCard } from '@/components/previews/StaticFilePreview'
import DatasheetPreview from '@/components/DatasheetPreview'
import { isSpreadsheetFile } from '@/lib/spreadsheetFile'
import { OVERLAY_PANEL_CLASS } from '@/components/previews/overlayPanel'
import PreviewCloseButton from '@/components/previews/PreviewCloseButton'
import { escapeBelongsToInput } from '@/components/previews/escapeBelongsToInput'
import { PdfJsViewer, type PdfJsViewerHandle } from '@/components/PdfViewer/PdfJsViewer'
import { ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_RASTER, ZOOM_MAX_PERCENT_VECTOR } from '@/constants/zoom'
import type { PnidZoomHandle } from '@/routes/GraphEditor/PnidRenderer'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { ANNOTATOR_READONLY_MESSAGE, notifyAnnotatorBlocked } from '@/service/annotatorAccess'

/**
 * The P&ID editor, read-only — the SAME component the Intelligent Board's
 * preview mounts for a drawing (`DocumentPreviewShape/DocumentPreview`).
 *
 * Lazy, because it is the whole editor (d3, the renderer, its panels) and only
 * a drawing on the Detected tab needs it; the Data Catalog's own previews and
 * every document preview must not pay for it in their chunk.
 */
const EditorWrapper = lazy(() => import('@/routes/GraphEditor/EditorWrapper'))

/** Which render the overlay is showing. Figma 13, frames 1320:209572 / 1325:231422. */
type PreviewView = 'original' | 'detected'

/**
 * One in-flight download per document, shared by every mount that wants it.
 *
 * The board's `drawingInFlight` (SheetsCanvas) for the same reason, and the
 * reason is measurable in dev: the app renders inside `React.StrictMode`
 * (`main.tsx`), which mounts every component, unmounts it and mounts it again.
 * Without this, opening one preview pulled the whole multi-megabyte file
 * TWICE — which is a large part of what "the Original tab is loading a lot"
 * feels like on localhost, and is invisible in a production build.
 *
 * Deliberately not a result cache: it is deleted once settled, so a document
 * re-opened later is fetched again rather than held in memory for the session.
 */
const documentInFlight = new Map<string, Promise<Blob>>()

function sharedDocumentDownload(docId: string): Promise<Blob> {
  const existing = documentInFlight.get(docId)
  if (existing) return existing
  const started = apiService
    .downloadDocument(docId)
    .finally(() => documentInFlight.delete(docId))
  documentInFlight.set(docId, started)
  return started
}

interface DocumentPreviewOverlayProps {
  docId: string
  docName: string
  onClose: () => void
  /**
   * Draw the Original | Detected toggle, the "Close Esc" button and the zoom
   * bar — the preview Figma section 13 specifies for tag mapping.
   *
   * Opt-in, and off by default, so the Data Catalog's own preview keeps the
   * filename card it was drawn with. When on, the overlay OPENS on Detected:
   * the annotated render is the reason a reviewer opened the preview at all.
   */
  withDetected?: boolean
  /**
   * The graph document behind a drawing, when the file has one.
   *
   * Detected mounts the P&ID editor on it, read-only — the same view the
   * Intelligent Board's preview shows for a drawing: the full-resolution page
   * with its detected symbols, tags and connections drawn as vectors, panned
   * and zoomed by the editor itself, opened zoomed onto the tag's node. It used to show `/thumbnail` of this
   * document instead, a 400px-wide PNG (`THUMBNAIL_WIDTH`) stretched across a
   * 1400px panel, which read as a blurred, wrong preview next to the board's.
   */
  graphId?: string | null
  /**
   * The node the reader came for on a drawing — the tag's own symbol.
   *
   * Handed to the editor the way `?highlightNode=` is on the graph-editor
   * route, so the preview opens zoomed onto the tag, selected, rather than
   * on a whole sheet the reviewer then has to search.
   */
  highlightNodeId?: string | null
  /**
   * The text the reader came for — a tag's mention, as THIS document spells it.
   *
   * A document has no node to zoom to and no column to shade, so the way to
   * land a reviewer on the tag is the document's own search: the PDF opens
   * already searching for it, on the page it is on, with every hit highlighted.
   * The board does the same thing for a drawing (`highlightNode`) and the
   * timeseries overlay for a sheet (`highlightColumn`); this is the third.
   */
  highlightText?: string | null
}

export default function DocumentPreviewOverlay({
  docId,
  docName,
  onClose,
  withDetected = false,
  graphId,
  highlightNodeId,
  highlightText,
}: DocumentPreviewOverlayProps) {
  const { t } = useTranslation()
  /**
   * The graph document behind a drawing, when there is one.
   *
   * Only a graph document carries the detections — it is a DIFFERENT document
   * from the drawing, so `graphId` absent (or equal to `docId`) means there is
   * no graph to open the editor on. That is the board's own rule
   * (`SheetsCanvas::fetchSourceThumbnail`).
   *
   * It decides what Detected shows, NOT whether Detected opens: with a graph,
   * the editor; without one, a server render of the page, or the file itself.
   * Detected always opens, and always shows something — see `detectedKind`.
   */
  const annotatedDocId = withDetected && graphId && graphId !== docId ? graphId : null
  /** Detected IS the editor: a drawing with a graph, on the Detected tab. */
  const editorOnDetected = Boolean(annotatedDocId)
  /**
   * A mention to land on, and no annotated render that could show it better.
   *
   * The preview OPENS on Detected. For a document that meant the flat page-1
   * thumbnail, captioned "no detections" — and the mention search, which only
   * the file renderer runs, never ran until the reader chose Original by hand
   * (Abdullah, #34226). What "detected" means for a document is the passage
   * that names the tag, so Detected renders the file itself, already searching,
   * whenever there is a mention and no graph render to prefer. A drawing keeps
   * its annotated render: its detections are drawn, not written.
   */
  const landsOnMention = withDetected && Boolean(highlightText) && !annotatedDocId
  /**
   * A spreadsheet or a timeseries export: no page to render, no download worth
   * making, and a viewer of its own.
   *
   * Every candidate the Edit Mapping eye can open is one of these (they come
   * from `timeseries_tags`), and this overlay used to show them as a static
   * placeholder card with "has no page to show here" under it — Abdullah,
   * #34136: "Mount DatasheetPreview / the appropriate source viewer inside the
   * overlay before removing the existing viewer route." The sheet viewer is the
   * same one `/timeseries/:id` and the mapped-tag overlay mount, landed on the
   * tag's column when there is one; it fetches its own rows, so the file is
   * not downloaded and no page render is asked for. Both tabs show it: a
   * sheet has no "original page" and no "detected render" to differ on.
   */
  const isSheet = isSpreadsheetFile(docName)
  /**
   * An annotator's access is P&IDs only. `/datasheet/:id` and `/timeseries/:id`
   * sit behind AnnotatorRestrictedRoute and the mapped column's handler checks
   * the role before opening its overlay, so mounting the same sheet viewer here
   * would be the one way round both (Abdullah, #34397). Same check, same
   * notice, in place of the viewer — the tabs and the file name still show, so
   * the reviewer sees WHAT was blocked.
   */
  const role = useAppStore((s) => s.user?.role)
  const sheetBlocked = isSheet && isAnnotatorRole(role)
  useEffect(() => {
    if (sheetBlocked) notifyAnnotatorBlocked()
  }, [sheetBlocked, docId])
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [fileType, setFileType] = useState<'image' | 'pdf' | 'other'>('pdf')
  const [loading, setLoading] = useState(true)
  /**
   * Whether a Detected view can exist for this file at all.
   *
   * It cannot for a spreadsheet. `/v1/index/:id/thumbnail` answers 404 for a
   * timeseries export — measured on dev against `Apr-10-Oct-Dec-2025.xlsx` —
   * because there is no page to render, let alone symbols to draw on it. The
   * sheet viewer is the one view a sheet has, so it opens on Original, in
   * that viewer, and is offered no toggle: a tab that can only repeat the
   * other tab is worse than no tab. The frame draws this preview against a
   * P&ID, which is the file that HAS both views, and a drawing or a document
   * still opens on Detected — "detected should open when we click preview".
   */
  const canDetect = withDetected && !isSheet
  const [view, setView] = useState<PreviewView>(canDetect ? 'detected' : 'original')
  /** The render Detected is showing. */
  const [detectedUrl, setDetectedUrl] = useState<string | null>(null)
  /**
   * WHICH render Detected ended up with, for a file with NO graph document —
   * a drawing with one mounts the editor and never reaches this. The panel
   * captions a flat page rather than passing it off as a detected view: a
   * reviewer handed a flat page silently would read the file as having no
   * detections on it.
   *
   *   'flat' — the document's own rendered page. No graph exists for this
   *            file, and this is the same image the board draws on its card.
   *   'file' — no server render at all; fall through to what Original shows,
   *            which for a spreadsheet is the static card.
   */
  type DetectedKind = 'flat' | 'file'
  const [detectedKind, setDetectedKind] = useState<DetectedKind>('file')
  const [detectedLoading, setDetectedLoading] = useState(withDetected)
  /** Page the PDF viewer is on, when one is mounted (a `highlightText` preview). */
  const [pdfPage, setPdfPage] = useState(1)
  const [scale, setScale] = useState(1)
  // 'absolute': the preview is a plain CSS transform, so scale 1 IS 100%.
  const { percent, zoomIn, zoomOut, setPercent, canZoomIn, canZoomOut } = useZoom({
    model: 'absolute',
    scale,
    onApply: setScale,
  })
  /**
   * A searched PDF renders through pdf.js, which owns its own scale — a CSS
   * transform over it would blur the text layer and misplace the highlight.
   * So while that viewer is mounted the zoom bar drives IT, through the
   * viewer's imperative handle, and the readout follows the viewer through
   * `onZoomChange`. Abdullah, #34226: the bar changed the percentage while
   * the page stayed the same size, and the viewer's own toolbar is hidden.
   */
  const pdfRef = useRef<PdfJsViewerHandle | null>(null)
  const [pdfMounted, setPdfMounted] = useState(false)
  const [pdfPercent, setPdfPercent] = useState(100)
  const attachPdf = useCallback((handle: PdfJsViewerHandle | null) => {
    pdfRef.current = handle
    setPdfMounted(handle !== null)
  }, [])
  /**
   * The editor owns its zoom the same way: d3 drives the canvas transform,
   * so a CSS scale over it would blur the drawing and misplace every box.
   * While the editor is on screen the bar drives IT, through the handle the
   * canvas assigns, and the readout follows the canvas (wheel zoom included).
   * The editor draws no pill of its own here (`hideChrome`) — Figma 13 puts
   * the one zoom bar at the screen corner, outside the panel.
   */
  const editorZoomRef = useRef<PnidZoomHandle | null>(null)
  const [editorPercent, setEditorPercent] = useState(100)
  const editorMounted = view === 'detected' && editorOnDetected
  const zoomBar = editorMounted
    ? {
        percent: editorPercent,
        zoomIn: () => editorZoomRef.current?.zoomIn(),
        zoomOut: () => editorZoomRef.current?.zoomOut(),
        setPercent: (next: number) => editorZoomRef.current?.setPercent(next),
        canZoomIn: editorPercent < ZOOM_MAX_PERCENT_VECTOR,
        canZoomOut: editorPercent > ZOOM_MIN_PERCENT,
        fit: () => editorZoomRef.current?.fit(),
      }
    : pdfMounted
    ? {
        percent: pdfPercent,
        zoomIn: () => pdfRef.current?.zoomIn(),
        zoomOut: () => pdfRef.current?.zoomOut(),
        setPercent: (next: number) => pdfRef.current?.setZoomPercent(next),
        canZoomIn: pdfPercent < ZOOM_MAX_PERCENT_RASTER,
        canZoomOut: pdfPercent > ZOOM_MIN_PERCENT,
        fit: () => pdfRef.current?.resetZoom(),
      }
    : {
        percent,
        zoomIn,
        zoomOut,
        setPercent,
        canZoomIn,
        canZoomOut,
        fit: () => setScale(1),
      }
  // Optimistic rename overlay (docName arrives as a prop with no setter).
  const [nameOverride, setNameOverride] = useState<string | null>(null)
  const displayName = nameOverride ?? docName
  const renameDocument = useRenameDocument()
  const handleRename = (newName: string) => {
    void renameDocument(docId, newName, { previousName: displayName, applyName: setNameOverride })
  }

  // Drop a stale optimistic rename if this overlay is reused for another doc,
  // and put the toggle back to the opening tab: the previous document's
  // fallback to Original is an answer about THAT document, not this one.
  useEffect(() => {
    setNameOverride(null)
    setView(withDetected ? 'detected' : 'original')
  }, [docId, withDetected])

  /**
   * What Detected shows for a file with NO graph document — and it always
   * shows SOMETHING.
   *
   * A drawing with a graph mounts the editor (`editorOnDetected`), which loads
   * its own page image and graph, so nothing is fetched here for it. For
   * everything else this is the board's own ladder
   * (`SheetsCanvas::fetchSourceThumbnail`) from the flat arm down:
   *
   *  1. the document's OWN render, which is flat. Every indexed file has one,
   *     and it is what the board draws on its cards.
   *  2. the file itself, so a spreadsheet shows the same static card Original
   *     does. Detected being a bare sentence was the reported "it says no
   *     detected view for this file" — the tab a reviewer opens on has to have
   *     a picture in it.
   */
  useEffect(() => {
    // Nothing to fetch when there is no Detected tab to fill. The Data
    // Catalog's own callers (`MergeAsSetModal`) never draw one, so this
    // otherwise fetched a render that could not be displayed — and, on a
    // retryable 502/503, asked for it three times. Flagged by Greptile.
    if (!withDetected || editorOnDetected || landsOnMention || isSheet) {
      // Nothing to fetch: no Detected tab, Detected is the editor, Detected
      // is the searched file, or the file is a sheet, which has no page
      // render at all.
      setDetectedLoading(false)
      return
    }
    let revoked = false
    let url: string | null = null

    setDetectedLoading(true)
    setDetectedUrl(null)
    setDetectedKind('file')

    const abandoned = () => {
      if (revoked) throw new Error('overlay closed')
    }

    // WITH the retry ladder: the route answers 503 while a render is still
    // being generated (FS-482), and treating that first answer as final is
    // what made Detected report nothing for a page seconds from ready.
    fetchThumbnailWithRetry(docId, abandoned)
      .then((blob) => {
        if (revoked) return
        url = URL.createObjectURL(blob)
        setDetectedUrl(url)
        setDetectedKind('flat')
      })
      .catch((err) => {
        if (revoked) return
        const status = (err as { response?: { status?: number } })?.response?.status ?? 0
        // 'file' is already the initial kind: the panel renders whatever
        // Original renders, which is never nothing. The status only reaches
        // the console: there is no empty state left to explain.
        console.warn(`[preview] no server render for ${docId} (HTTP ${status})`)
      })
      .finally(() => {
        if (!revoked) setDetectedLoading(false)
      })

    return () => {
      revoked = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [docId, withDetected, editorOnDetected, landsOnMention, isSheet])

  /**
   * The Original render downloads the WHOLE file, which for a P&ID is ~2 MB.
   *
   * It starts on mount, and NOTHING may be allowed to come first.
   *
   * Two previous attempts to be clever here both made the wait worse, and the
   * shape of the mistake was the same each time: putting the multi-megabyte
   * transfer downstream of something else. Gating it on the tab click moved the
   * entire wait in front of the reviewer. Gating it on the Detected fetch
   * having ANSWERED was worse — `getDocumentThumbnail` carries the default 45s
   * deadline, and a thumbnail route that hangs rather than refusing therefore
   * held Original for 45s before it could send its first byte (longer still if
   * the route answers a retryable 502/503, which re-arms the ladder). That is
   * the reported "the Original tab is buffering".
   *
   * The competition it was avoiding is the smaller problem, and the thumbnail
   * is 25-39 KB against this file's megabytes — it is not the one being starved.
   *
   * It is also fetched ONCE per document. The effect used to be keyed on a
   * `needsOriginal` derived from the tab, so every toggle back to Original
   * revoked the object URL and downloaded the whole file again: the same
   * transfer, from scratch, on a tab already waited for once. These two refs
   * are what hold it across the toggle.
   */
  const requestedDocRef = useRef<string | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  // Drop the previous document's file when the overlay moves to another one —
  // and on unmount. This is the ONLY place the object URL is revoked, so
  // switching tabs cannot pull the render out from under itself.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
      requestedDocRef.current = null
      setBlobUrl(null)
      setLoading(true)
    }
  }, [docId])

  useEffect(() => {
    if (isSheet) {
      // The sheet viewer fetches its own rows; the file itself is not shown.
      setLoading(false)
      return
    }
    if (requestedDocRef.current === docId) return // in flight, or already here
    requestedDocRef.current = docId
    let revoked = false
    setLoading(true)
    sharedDocumentDownload(docId).then((blob) => {
      if (revoked) return
      const url = URL.createObjectURL(blob)
      blobUrlRef.current = url
      setBlobUrl(url)
      // An iframe renders a PDF and an image inline; anything else it DOWNLOADS,
      // which is what "clicking Original downloads the file" was. Spreadsheets
      // reach this overlay now that every row previews in it, so the kind has to
      // be read from the blob rather than assumed to be a PDF.
      setFileType(
        blob.type.startsWith('image/')
          ? 'image'
          : blob.type === 'application/pdf'
            ? 'pdf'
            : 'other',
      )
      setLoading(false)
    }).catch(() => {
      if (revoked) return
      // Let the next visit retry rather than latching "not available" onto a
      // document whose download merely failed.
      requestedDocRef.current = null
      setLoading(false)
    })
    return () => {
      revoked = true
    }
  }, [docId, isSheet])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // A search box inside the panel owns its own Escape; see the helper.
      if (e.key === 'Escape' && !escapeBelongsToInput(e)) onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  /**
   * The file itself — the Original render, and Detected's last resort.
   *
   * Shared by both tabs so Detected can never be emptier than Original: if
   * there is a picture to show, the tab the preview OPENS on shows it.
   */
  const renderFile = () => {
    if (sheetBlocked) {
      return (
        <div
          role="status"
          className="flex h-full w-full items-center justify-center px-6 text-center"
        >
          <span className="text-sm text-muted-foreground">{ANNOTATOR_READONLY_MESSAGE}</span>
        </div>
      )
    }
    if (isSheet) {
      return (
        <DatasheetPreview
          docId={docId}
          className="h-full"
          highlightColumn={highlightText ?? undefined}
        />
      )
    }
    if (loading) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      )
    }
    if (!blobUrl) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-sm text-muted-foreground">{t('data_catalog:preview.not_available')}</span>
        </div>
      )
    }
    // No inline renderer for this kind of file, so DO NOT hand it to an iframe —
    // that is a download, not a preview. The board already draws a static grid
    // for exactly these files; the overlay shows the same one so the two
    // surfaces agree.
    if (fileType === 'other') {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6">
          <div className="h-[220px] w-[340px] overflow-hidden rounded-md border border-border">
            <SpreadsheetCard docId={docId} />
          </div>
          <span className="text-sm text-muted-foreground">
            {t('data_catalog:tag_mapping.preview.no_inline', {
              defaultValue: '{{name}} has no page to show here.',
              name: displayName,
            })}
          </span>
        </div>
      )
    }
    if (fileType === 'image') {
      return (
        <img
          src={blobUrl}
          alt={displayName}
          className="absolute inset-0 w-full h-full origin-center object-cover pointer-events-none"
          style={withDetected ? { transform: `scale(${scale})` } : undefined}
          draggable={false}
        />
      )
    }
    // A tag to find, so render through the PDF viewer rather than the browser's
    // built-in one: it is the only path that can search the text layer, jump to
    // the page the mention is on and highlight it. Without one, the iframe stays
    // — it is lighter, and there is nothing to look for.
    if (highlightText && fileType === 'pdf') {
      return (
        <PdfJsViewer
          ref={attachPdf}
          url={blobUrl}
          currentPage={pdfPage}
          onPageChange={setPdfPage}
          onTotalPagesChange={() => {}}
          documentId={docId}
          documentName={displayName}
          hideToolbar
          initialSearch={highlightText}
          onZoomChange={setPdfPercent}
        />
      )
    }
    return (
      <iframe
        src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
        title={displayName}
        className="w-full h-full border-0"
      />
    )
  }

  return (
    <div
      className="fixed inset-0 z-[9999999] overflow-hidden"
      style={{ backgroundColor: 'rgba(34, 34, 34, 0.5)' }}
      onClick={onClose}
    >
      {/* Top-left. The frame puts the Original | Detected toggle here on the
          tag-mapping preview, and the filename card here everywhere else. */}
      {/* Top-left. Figma 13 (1320:230890): a white Hover Card holding a muted
          tabs track, the selected trigger a white pill. The filename card stays
          for every other caller. */}
      {canDetect ? (
        <div
          className="absolute left-3.5 top-3.5 z-20 flex items-center gap-2 rounded-[8px] border border-border bg-popover p-1.5 shadow-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex h-8 items-center rounded-[10px] bg-muted p-[3px]" role="tablist">
            {(['original', 'detected'] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={view === option}
                onClick={() => setView(option)}
                className={cn(
                  'flex h-full items-center justify-center rounded-[8px] px-3 py-1 text-sm font-medium transition-colors',
                  view === option
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option === 'original'
                  ? t('data_catalog:tag_mapping.preview.original', { defaultValue: 'Original' })
                  : t('data_catalog:tag_mapping.preview.detected', { defaultValue: 'Detected' })}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="absolute top-3 left-3 z-20 bg-white dark:bg-dark-card rounded-[10px] shadow-xs border border-border px-3.5 py-2.5 h-[52px] w-[420px] flex items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <FileNameWithCopy
            name={displayName}
            className="!text-base !font-medium text-foreground"
            onRename={handleRename}
            stopPropagation
          />
        </div>
      )}

      {/* Top-right. Figma 13: the same white Hover Card, holding a white Close
          with the Esc key as a muted chip — one component, shared with the
          mapped-tag overlay. The Data Catalog's own preview keeps its X. */}
      {withDetected ? (
        <PreviewCloseButton onClose={onClose} />
      ) : (
        <button
          type="button"
          className="absolute top-3 right-3 z-20 bg-white dark:bg-dark-card rounded-[10px] shadow-xs border border-border h-[52px] w-[52px] flex items-center justify-center hover:bg-muted transition-colors"
          onClick={(e) => { e.stopPropagation(); onClose() }}
        >
          <X className="size-6 text-foreground" />
        </button>
      )}

      {/* The panel.
          Tag mapping shares ONE panel across its overlays, so previewing a file
          and previewing a mapped tag's sheet open the same size window
          (`OVERLAY_PANEL_CLASS`). The Data Catalog's own preview keeps the fixed
          card it was drawn with — a different surface, with its own chrome. */}
      <div
        className={cn(
          withDetected
            ? OVERLAY_PANEL_CLASS
            : 'absolute top-[100px] left-1/2 -translate-x-1/2 w-[1000px] h-[700px] bg-white dark:bg-dark-card dark:border dark:border-dark-border overflow-hidden',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'detected' ? (
          annotatedDocId ? (
            // The drawing in the P&ID editor, read-only, on its GRAPH document
            // — what the Intelligent Board's preview shows for the same sheet,
            // minus the editor's own chrome: the overlay draws the Original |
            // Detected toggle, the Close and the zoom bar (Figma 13). It opens
            // zoomed onto the tag's node, selected. The editor loads the full
            // page image and draws the detections as vectors, so zooming in
            // shows the drawing, not a stretched thumbnail.
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center">
                  <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <div className="relative h-full w-full">
                <EditorWrapper
                  key={annotatedDocId}
                  graphId={annotatedDocId}
                  initialZoom={1}
                  isReadOnly
                  highlightNodeId={highlightNodeId}
                  hideChrome
                  zoomHandleRef={editorZoomRef}
                  onZoomChange={setEditorPercent}
                />
              </div>
            </Suspense>
          ) : landsOnMention ? (
            // The file, opened on the mention — see `landsOnMention`. (A sheet
            // never reaches this branch: it has no Detected view, see
            // `canDetect`.)
            renderFile()
          ) : detectedLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : detectedUrl ? (
            <>
              <img
                src={detectedUrl}
                alt={displayName}
                className="h-full w-full origin-center object-contain"
                style={{ transform: `scale(${scale})` }}
                draggable={false}
              />
              {/* A FLAT page under a tab labelled "Detected" would read as a
                  drawing with nothing found on it, so it says which render this
                  is. The annotated one needs no caption — it IS the tab. */}
              {detectedKind === 'flat' && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-background/85 px-4 py-2 text-center text-xs text-muted-foreground backdrop-blur-sm">
                  {t('data_catalog:tag_mapping.preview.no_annotations', {
                    defaultValue: 'No detections on this file — showing the page itself.',
                  })}
                </div>
              )}
            </>
          ) : (
            // No server render of any kind. Show the FILE rather than a
            // sentence: the tab the preview opens on has to have a picture in
            // it, and for a spreadsheet that is the same static card the board
            // draws. This is what "it says no detected view for this file" was.
            renderFile()
          )
        ) : (
          renderFile()
        )}
      </div>

      {/* Zoom bar, bottom-left, as the frame draws it — the one zoom bar,
          whatever is in the panel: it drives the editor, the PDF viewer or a
          plain picture (see `zoomBar`). Not over a sheet: the sheet viewer
          carries its own zoom, and two would fight. */}
      {withDetected && !isSheet && (
        <div className="absolute bottom-3.5 left-3.5 z-20" onClick={(e) => e.stopPropagation()}>
          <ZoomControls
            percent={zoomBar.percent}
            onZoomIn={zoomBar.zoomIn}
            onZoomOut={zoomBar.zoomOut}
            onSetPercent={zoomBar.setPercent}
            canZoomIn={zoomBar.canZoomIn}
            canZoomOut={zoomBar.canZoomOut}
            onFit={zoomBar.fit}
          />
        </div>
      )}
    </div>
  )
}
